/**
 * Système de filtrage custom pour collections Shopify - Thème Turbo v7
 * Gère le filtrage par tags et métafields avec interface interactive
 *
 * Dépendances : snippet-collection-filters.liquid, snippet-product-metafields.liquid
 * Note : La config est chargée soit via window.__filterConfigUrl (injecté par Liquid)
 *        soit via la config par défaut intégrée.
 */

class FiltreCollection {
  constructor() {
    // Empêcher la double initialisation
    if (window.__filtreCollectionInitialisé) return;
    window.__filtreCollectionInitialisé = true;

    this.tousLesProduits = [];
    this.produitsFiltrés = [];
    this.configFiltre = null;
    this.donnéesMétafields = {};
    this.filtresActifs = {};
    this.paginationActuelle = 1;
    this.parPage = 24;
    this.sortieActuelle = '';
    this.chargeComplète = false;

    this.init();
  }

  /**
   * Initialisation du système de filtrage
   */
  async init() {
    console.log('[Filtre Custom] Initialisation...');

    // Cacher la pagination SEO Liquid (elle est là pour Googlebot uniquement)
    var seoPagination = document.querySelector('.seo-pagination');
    if (seoPagination) seoPagination.style.display = 'none';

    // Lire le numéro de page depuis l'URL si présent
    var urlParams = new URLSearchParams(window.location.search);
    var pageFromUrl = parseInt(urlParams.get('page'));
    if (pageFromUrl && pageFromUrl > 0) {
      this.paginationActuelle = pageFromUrl;
    }

    // Récupérer les éléments du DOM
    this.elementsFiltre = {
      inputs: document.querySelectorAll('.custom-filter-input'),
      compteur: document.getElementById('custom-filters-count'),
      pluriel: document.getElementById('custom-filters-plural'),
      btnAppliquer: document.getElementById('custom-filters-apply-btn'),
      sidebarFiltres: document.getElementById('custom-filters-sidebar'),
      btnFermer: document.getElementById('custom-filters-close'),
      btnToggleMobile: document.getElementById('custom-filters-toggle-mobile'),
      selectTri: document.getElementById('custom-sort-select'),
      selectParPage: document.getElementById('custom-per-page-select'),
      grilleProduits: document.getElementById('custom-filters-products'),
      aucunRésultat: document.getElementById('custom-filters-no-results'),
      toggles: document.querySelectorAll('.custom-filter-toggle')
    };

    // Vérifier que les éléments critiques existent
    if (!this.elementsFiltre.grilleProduits) {
      console.warn('[Filtre Custom] Grille produits non trouvée, abandon.');
      return;
    }

    // Configurer les événements IMMÉDIATEMENT (pas besoin d'attendre les données)
    this.configurerÉvénements();

    // PERFORMANCE: Différer le chargement lourd (products.json ~150KB) après le rendu initial
    // Utiliser requestIdleCallback ou setTimeout pour ne pas bloquer le LCP
    var self = this;
    var chargerDonnées = async function() {
      // Charger la config
      await self.chargerConfig();

      // Charger les métafields depuis le fichier JSON statique (tous les produits)
      await self.chargerMétafieldsDepuisFichier();

      // Charger tous les produits de la collection via l'API JSON
      await self.chargerTousLesProduits();

      // Restaurer les filtres depuis l'URL
      self.restaurerFiltresDepuisURL();

      // PERFORMANCE: Ne PAS remplacer le HTML Liquid si aucun filtre actif
      // Le HTML rendu par Liquid est déjà correct pour la page initiale
      // Remplacer le DOM provoque un nouveau LCP très tard (8s+)
      if (Object.keys(self.filtresActifs).length > 0) {
        self.afficherProduits();
      } else {
        // Juste mettre à jour le compteur sans toucher au DOM
        self.produitsFiltrés = self.tousLesProduits.slice();
        self.mettreÀJourCompteur();
        // Toujours afficher la pagination même sans filtre actif
        self.afficherPagination();
      }

      console.log('[Filtre Custom] Initialisé avec', self.tousLesProduits.length, 'produits');
    };

    // Utiliser requestIdleCallback si disponible, sinon setTimeout
    if ('requestIdleCallback' in window) {
      requestIdleCallback(function() { chargerDonnées(); }, { timeout: 2000 });
    } else {
      setTimeout(function() { chargerDonnées(); }, 100);
    }
  }

  /**
   * Charger la configuration des filtres
   */
  async chargerConfig() {
    // Essayer l'URL injectée par Liquid (la plus fiable)
    const configUrl = window.__filterConfigUrl;
    if (configUrl) {
      try {
        const réponse = await fetch(configUrl);
        if (réponse.ok) {
          this.configFiltre = await réponse.json();
          console.log('[Filtre Custom] Config chargée depuis asset');
          return;
        }
      } catch (erreur) {
        console.warn('[Filtre Custom] Erreur chargement config asset:', erreur);
      }
    }

    // Fallback : config par défaut
    console.warn('[Filtre Custom] Config non trouvée, utilisation de la config intégrée');
    this.configFiltre = this.obtenirConfigParDéfaut();
  }

  /**
   * Charger les métafields depuis le fichier JSON statique (tous les produits)
   * Fallback : bloc Liquid injecté dans la page
   */
  async chargerMétafieldsDepuisFichier() {
    // Méthode principale : fichier JSON statique avec TOUS les produits
    var metafieldsUrl = window.__metafieldsJsonUrl;
    if (!metafieldsUrl) {
      // Construire l'URL depuis le CDN Shopify
      var scripts = document.querySelectorAll('link[href*="collection-filter.css"]');
      if (scripts.length > 0) {
        var cssUrl = scripts[0].href;
        metafieldsUrl = cssUrl.replace('collection-filter.css', 'product-metafields-all.json').split('?')[0];
      }
    }

    if (metafieldsUrl) {
      try {
        var réponse = await fetch(metafieldsUrl);
        if (réponse.ok) {
          this.donnéesMétafields = await réponse.json();
          console.log('[Filtre Custom] Métafields chargées depuis fichier JSON pour', Object.keys(this.donnéesMétafields).length, 'produits');
          return;
        }
      } catch (erreur) {
        console.warn('[Filtre Custom] Erreur chargement fichier métafields:', erreur);
      }
    }

    // Fallback : bloc Liquid injecté dans la page (limité à la page paginée)
    var scriptMétafields = document.getElementById('product-metafields-data');
    if (scriptMétafields) {
      try {
        this.donnéesMétafields = JSON.parse(scriptMétafields.textContent);
        console.log('[Filtre Custom] Métafields chargées depuis Liquid pour', Object.keys(this.donnéesMétafields).length, 'produits (fallback)');
      } catch (erreur) {
        console.error('[Filtre Custom] Erreur parsing métafields Liquid:', erreur);
        this.donnéesMétafields = {};
      }
    } else {
      console.warn('[Filtre Custom] Aucune donnée métafields trouvée');
    }
  }

  /**
   * Charger TOUS les produits de la collection via l'API JSON
   */
  async chargerTousLesProduits() {
    const collectionHandle = this.obtenirCollectionHandle();
    if (!collectionHandle) {
      console.warn('[Filtre Custom] Handle de collection introuvable');
      return;
    }

    console.log('[Filtre Custom] Chargement produits:', collectionHandle);

    try {
      let pageActuelle = 1;
      let produitsChargés = [];
      let continuer = true;

      while (continuer) {
        const url = '/collections/' + collectionHandle + '/products.json?limit=250&page=' + pageActuelle;
        const réponse = await fetch(url);

        if (!réponse.ok) {
          continuer = false;
          break;
        }

        const données = await réponse.json();
        const produits = données.products || [];

        if (produits.length === 0) {
          continuer = false;
          break;
        }

        produitsChargés = produitsChargés.concat(produits);
        pageActuelle++;
      }

      // Construire la liste des produits en fusionnant API + métafields Liquid
      this.tousLesProduits = produitsChargés.map(function(produitAPI) {
        var idStr = String(produitAPI.id);
        var métafields = this.donnéesMétafields[idStr] || {};

        // Prix : l'API retourne le prix en centimes (string), les métafields en euros
        var prixMin = métafields.prix_min || (produitAPI.variants && produitAPI.variants.length > 0
          ? Math.min.apply(null, produitAPI.variants.map(function(v) { return parseFloat(v.price) || 0; }))
          : 0);
        var prixMax = métafields.prix_max || (produitAPI.variants && produitAPI.variants.length > 0
          ? Math.max.apply(null, produitAPI.variants.map(function(v) { return parseFloat(v.price) || 0; }))
          : 0);

        // Extraire les couleurs depuis les options du produit
        var couleurs = [];
        if (produitAPI.options) {
          produitAPI.options.forEach(function(opt) {
            var nomOption = (opt.name || '').toLowerCase();
            if (nomOption === 'couleur' || nomOption === 'color' || nomOption === 'colour') {
              couleurs = opt.values || [];
            }
          });
        }

        return {
          id: produitAPI.id,
          titre: produitAPI.title,
          prix: prixMin,
          prix_min: prixMin,
          prix_max: prixMax,
          image: produitAPI.images && produitAPI.images.length > 0 ? produitAPI.images[0].src : '',
          lien: '/products/' + produitAPI.handle,
          handle: produitAPI.handle,
          tags: Array.isArray(produitAPI.tags) ? produitAPI.tags : (produitAPI.tags ? produitAPI.tags.split(', ') : []),
          métafields: métafields,
          couleurs: couleurs
        };
      }.bind(this));

      this.produitsFiltrés = this.tousLesProduits.slice();
      this.chargeComplète = true;

      // Mettre à jour le compteur initial
      this.mettreÀJourCompteur();

      console.log('[Filtre Custom]', this.tousLesProduits.length, 'produits chargés');
    } catch (erreur) {
      console.error('[Filtre Custom] Erreur chargement produits:', erreur);
    }
  }

  /**
   * Configurer les événements des filtres
   */
  configurerÉvénements() {
    var self = this;

    // Événements filtres - appliquer automatiquement à chaque changement
    this.elementsFiltre.inputs.forEach(function(input) {
      input.addEventListener('change', function() {
        self.mettreÀJourFiltres();
        self.appliquerFiltres();
      });
    });

    // Événements toggles accordion
    this.elementsFiltre.toggles.forEach(function(toggle) {
      toggle.addEventListener('click', function(e) {
        self.basculeAccordion(e);
      });
    });

    // Bouton appliquer (surtout utile sur mobile pour fermer le drawer)
    if (this.elementsFiltre.btnAppliquer) {
      this.elementsFiltre.btnAppliquer.addEventListener('click', function() {
        self.appliquerFiltres();
        self.elementsFiltre.sidebarFiltres.classList.remove('active');
      });
    }

    // Sélect tri
    if (this.elementsFiltre.selectTri) {
      this.elementsFiltre.selectTri.addEventListener('change', function(e) {
        self.changeSortie(e.target.value);
      });
    }

    // Sélect par page
    if (this.elementsFiltre.selectParPage) {
      this.elementsFiltre.selectParPage.addEventListener('change', function(e) {
        self.parPage = parseInt(e.target.value);
        self.paginationActuelle = 1;
        self.afficherProduits();
      });
    }

    // Bouton mobile ouvrir
    if (this.elementsFiltre.btnToggleMobile) {
      this.elementsFiltre.btnToggleMobile.addEventListener('click', function() {
        self.elementsFiltre.sidebarFiltres.classList.add('active');
        document.body.style.overflow = 'hidden';
      });
    }

    // Bouton fermer
    if (this.elementsFiltre.btnFermer) {
      this.elementsFiltre.btnFermer.addEventListener('click', function() {
        self.elementsFiltre.sidebarFiltres.classList.remove('active');
        document.body.style.overflow = '';
      });
    }

    // Overlay mobile : fermer en cliquant en dehors
    document.addEventListener('click', function(e) {
      if (
        self.elementsFiltre.sidebarFiltres.classList.contains('active') &&
        !e.target.closest('.custom-filters-sidebar') &&
        !e.target.closest('.custom-filters-toggle-mobile')
      ) {
        self.elementsFiltre.sidebarFiltres.classList.remove('active');
        document.body.style.overflow = '';
      }
    });

    // Gérer le bouton retour/avant du navigateur
    window.addEventListener('popstate', function(e) {
      var params = new URLSearchParams(window.location.search);
      var page = parseInt(params.get('page')) || 1;
      self.paginationActuelle = page;
      self.afficherProduits();
    });
  }

  /**
   * Basculer accordion des filtres
   */
  basculeAccordion(événement) {
    var bouton = événement.currentTarget;
    var filterName = bouton.getAttribute('data-filter');
    var options = document.querySelector(
      '.custom-filter-options[data-filter="' + filterName + '"]'
    );

    if (!options) return;

    var estOuvert = options.classList.contains('active');

    // Basculer l'icône chevron
    bouton.classList.toggle('active');

    // Basculer le panneau d'options
    if (estOuvert) {
      options.classList.remove('active');
      bouton.classList.remove('active');
    } else {
      options.classList.add('active');
      bouton.classList.add('active');
    }
  }

  /**
   * Mettre à jour l'état des filtres depuis les inputs cochés
   */
  mettreÀJourFiltres() {
    this.filtresActifs = {};

    this.elementsFiltre.inputs.forEach(function(input) {
      if (input.checked) {
        var filterName = input.getAttribute('data-filter');
        var value = input.value;

        if (!this.filtresActifs[filterName]) {
          this.filtresActifs[filterName] = [];
        }

        this.filtresActifs[filterName].push(value);
      }
    }.bind(this));

    this.mettreÀJourCompteur();
    this.sauvegarderFiltresVersURL();
  }

  /**
   * Appliquer les filtres et afficher les résultats
   */
  appliquerFiltres() {
    var self = this;
    this.produitsFiltrés = this.tousLesProduits.filter(function(produit) {
      return self.produitCorrespondFiltre(produit);
    });

    this.paginationActuelle = 1;
    this.afficherProduits();
  }

  /**
   * Vérifier si un produit correspond aux filtres actifs
   * Logique : ET entre catégories, OU à l'intérieur d'une catégorie
   */
  produitCorrespondFiltre(produit) {
    var actifs = Object.entries(this.filtresActifs);
    if (actifs.length === 0) return true;

    for (var i = 0; i < actifs.length; i++) {
      var categorie = actifs[i][0];
      var valeurs = actifs[i][1];
      var correspondCategorie = false;

      for (var j = 0; j < valeurs.length; j++) {
        if (this.produitCorrespond(produit, categorie, valeurs[j])) {
          correspondCategorie = true;
          break;
        }
      }

      if (!correspondCategorie) return false;
    }

    return true;
  }

  /**
   * Vérifier si un produit correspond à un filtre spécifique
   */
  produitCorrespond(produit, categorie, valeur) {
    // Filtres basés sur les tags uniquement
    var filtresTags = ['prix'];
    if (filtresTags.indexOf(categorie) !== -1) {
      return produit.tags.some(function(tag) {
        return tag.toLowerCase().trim() === valeur.toLowerCase().trim();
      });
    }

    // Filtres basés sur les métafields (la majorité des filtres)
    var mapMétafields = {
      forme: 'forme_de_la_robe',
      longueur: 'longueur',
      col: 'type_de_col',
      manches: 'manches_et_bretelles',
      dos: 'style_du_dos',
      tissu: 'tissu',
      lieux: 'lieux_de_mariage',
      style: 'style',
      saison: 'saison',
      ornement: 'ornement',
      taille: 'taille'
    };

    if (mapMétafields[categorie]) {
      var métafieldValue = produit.métafields[mapMétafields[categorie]];
      if (!métafieldValue) return false;

      // Normaliser la valeur métafield (peut être un array, un string JSON array, ou un simple string)
      var valeursMetafield = this.normaliserMétafieldValue(métafieldValue);
      var valeurRecherche = valeur.toLowerCase().trim();

      return valeursMetafield.some(function(v) {
        return v.toLowerCase().trim() === valeurRecherche;
      });
    }

    return false;
  }

  /**
   * Normaliser une valeur de métafield en array de strings
   * Gère : arrays JS, strings JSON "[\"val1\", \"val2\"]", ou simples strings
   */
  normaliserMétafieldValue(valeur) {
    if (Array.isArray(valeur)) return valeur;
    if (typeof valeur !== 'string') return [];
    if (!valeur || valeur === '') return [];

    // Tenter de parser comme JSON (array)
    var trimmed = valeur.trim();
    if (trimmed.charAt(0) === '[') {
      try {
        var parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // Si le parse échoue, extraire manuellement les valeurs entre guillemets
        var résultat = [];
        var regex = /"([^"]+)"/g;
        var match;
        while ((match = regex.exec(trimmed)) !== null) {
          résultat.push(match[1]);
        }
        if (résultat.length > 0) return résultat;
      }
    }

    // Simple string
    return [valeur];
  }

  /**
   * Mettre à jour le compteur de produits
   */
  mettreÀJourCompteur() {
    if (!this.elementsFiltre.compteur) return;

    var compteur = this.tousLesProduits.length;

    if (Object.keys(this.filtresActifs).length > 0) {
      var self = this;
      compteur = this.tousLesProduits.filter(function(produit) {
        return self.produitCorrespondFiltre(produit);
      }).length;
    }

    this.elementsFiltre.compteur.textContent = compteur;

    if (this.elementsFiltre.pluriel) {
      this.elementsFiltre.pluriel.textContent = compteur > 1 ? 's' : '';
    }
  }

  /**
   * Afficher les produits filtrés avec pagination
   */
  afficherProduits() {
    var début = (this.paginationActuelle - 1) * this.parPage;
    var fin = début + this.parPage;
    var produitsÀAfficher = this.produitsFiltrés.slice(début, fin);

    if (produitsÀAfficher.length === 0) {
      this.elementsFiltre.grilleProduits.innerHTML = '';
      if (this.elementsFiltre.aucunRésultat) {
        this.elementsFiltre.aucunRésultat.style.display = 'block';
      }
      this.mettreÀJourCompteur();
      return;
    }

    if (this.elementsFiltre.aucunRésultat) {
      this.elementsFiltre.aucunRésultat.style.display = 'none';
    }

    var self = this;
    this.elementsFiltre.grilleProduits.innerHTML = produitsÀAfficher
      .map(function(produit) { return self.créerCarteProduit(produit); })
      .join('');

    this.mettreÀJourCompteur();
    this.afficherPagination();

    // Scroll vers le haut de la grille si on a changé de page
    if (this.paginationActuelle > 1) {
      this.elementsFiltre.grilleProduits.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /**
   * Mapping couleurs françaises → codes hex
   */
  obtenirCouleurHex(nomCouleur) {
    var couleurMap = {
      'blanc': { bg: '#FFFFFF', border: '#d0d0d0' },
      'blanche': { bg: '#FFFFFF', border: '#d0d0d0' },
      'ivoire': { bg: '#FFFFF0', border: '#d0d0b0' },
      'champagne': { bg: '#F7E7CE', border: '#d4c4a8' },
      'comme sur les photos': { bg: '#1a1a1a', border: '#000000' },
      'ivoire/doublure champagne': { bg: 'linear-gradient(135deg, #FFFFF0 50%, #F7E7CE 50%)', border: '#d0d0b0' },
      'noir': { bg: '#000000', border: '#333333' },
      'rose': { bg: '#FFB6C1', border: '#e0a0a8' },
      'rouge': { bg: '#C41E3A', border: '#a01830' },
      'bleu': { bg: '#4169E1', border: '#3050b0' }
    };
    var clé = (nomCouleur || '').toLowerCase().trim();
    return couleurMap[clé] || { bg: '#CCCCCC', border: '#999999' };
  }

  /**
   * Créer les swatches de couleur pour une carte collection
   */
  créerSwatchsCouleur(couleurs) {
    if (!couleurs || couleurs.length === 0) return '';
    var self = this;
    var swatchsHTML = couleurs.map(function(couleur) {
      var c = self.obtenirCouleurHex(couleur);
      var isGradient = c.bg.indexOf('linear-gradient') !== -1;
      var style = isGradient
        ? 'background:' + c.bg + ';border-color:' + c.border
        : 'background-color:' + c.bg + ';border-color:' + c.border;
      return '<span class="collection-color-dot" title="' + self.échapperHTML(couleur) + '" style="' + style + '"></span>';
    }).join('');
    return '<div class="collection-color-swatches">' + swatchsHTML + '</div>';
  }

  /**
   * Créer le HTML d'une carte produit
   */
  créerCarteProduit(produit) {
    var prixAffichage;
    if (produit.prix_min && produit.prix_max && produit.prix_min !== produit.prix_max) {
      prixAffichage = this.formatPrix(produit.prix_min) + ' - ' + this.formatPrix(produit.prix_max);
    } else {
      prixAffichage = this.formatPrix(produit.prix_min || produit.prix || 0);
    }

    var swatchsHTML = this.créerSwatchsCouleur(produit.couleurs);

    // Optimiser l'URL image Shopify pour générer des srcset
    var imgBase = produit.image;
    var imgSrcset = '';
    var imgSrc = imgBase;
    if (imgBase && imgBase.indexOf('cdn.shopify.com') !== -1) {
      // Nettoyer l'URL: retirer tout suffixe _WIDTHx existant et .progressive
      var imgClean = imgBase.replace(/_\d+x(\.|@)/g, '$1').replace(/\.progressive\./g, '.');
      // Fonction helper pour générer URL à une taille donnée
      var makeSize = function(url, w) {
        // Si l'URL utilise le paramètre width (nouveau format Shopify)
        if (url.indexOf('width=') !== -1) {
          return url.replace(/width=\d+/, 'width=' + w);
        }
        // Sinon, insérer _WIDTHx avant l'extension
        return url.replace(/(\.(jpg|jpeg|png|webp|gif))/i, '_' + w + 'x$1');
      };
      var img200 = makeSize(imgClean, 200);
      var img300 = makeSize(imgClean, 300);
      var img400 = makeSize(imgClean, 400);
      var img600 = makeSize(imgClean, 600);
      var img800 = makeSize(imgClean, 800);
      var img1000 = makeSize(imgClean, 1000);
      // Le src par défaut est 400px (taille d'affichage typique desktop)
      imgSrc = img600;
      imgSrcset = ' srcset="' + img200 + ' 200w, ' + img300 + ' 300w, ' + img400 + ' 400w, ' + img600 + ' 600w, ' + img800 + ' 800w, ' + img1000 + ' 1000w" sizes="(max-width: 480px) 50vw, (max-width: 768px) 35vw, (max-width: 1200px) 30vw, 350px"';
    }

    return '<a href="' + produit.lien + '" class="custom-filters-product-card" data-product-id="' + produit.id + '">' +
      '<div class="custom-filters-product-image">' +
        '<img src="' + imgSrc + '"' + imgSrcset + ' alt="' + this.échapperHTML(produit.titre) + '" loading="lazy" decoding="async">' +
      '</div>' +
      '<div class="custom-filters-product-info">' +
        '<h3 class="custom-filters-product-title">' + this.échapperHTML(produit.titre) + '</h3>' +
        '<div class="custom-filters-product-price">' + prixAffichage + '</div>' +
        swatchsHTML +
      '</div>' +
    '</a>';
  }

  /**
   * Construire l'URL de pagination SEO-friendly
   */
  construireUrlPage(page) {
    var collectionHandle = this.obtenirCollectionHandle();
    var basePath = '/collections/' + collectionHandle;
    var params = new URLSearchParams(window.location.search);
    
    // Nettoyer les anciens params de page
    params.delete('page');
    
    if (page > 1) {
      params.set('page', page);
    }
    
    var queryString = params.toString();
    return basePath + (queryString ? '?' + queryString : '');
  }

  /**
   * Afficher la pagination avec des liens <a href> SEO-friendly
   */
  afficherPagination() {
    // Supprimer l'ancienne pagination
    var anciennePagination = document.getElementById('custom-filters-pagination');
    if (anciennePagination) anciennePagination.remove();

    var totalPages = Math.ceil(this.produitsFiltrés.length / this.parPage);
    if (totalPages <= 1) return;

    var self = this;
    var paginationHTML = '<nav id="custom-filters-pagination" class="custom-filters-pagination" aria-label="Pagination">';

    // Lien précédent
    if (this.paginationActuelle > 1) {
      var prevUrl = this.construireUrlPage(this.paginationActuelle - 1);
      paginationHTML += '<a href="' + prevUrl + '" class="custom-pagination-btn" data-page="' + (this.paginationActuelle - 1) + '" rel="prev">&laquo; Précédent</a>';
    }

    // Numéros de pages avec troncature intelligente
    var startPage = Math.max(1, this.paginationActuelle - 2);
    var endPage = Math.min(totalPages, this.paginationActuelle + 2);

    // Toujours afficher la première page
    if (startPage > 1) {
      var url1 = this.construireUrlPage(1);
      paginationHTML += '<a href="' + url1 + '" class="custom-pagination-btn custom-pagination-num" data-page="1">1</a>';
      if (startPage > 2) {
        paginationHTML += '<span class="custom-pagination-ellipsis">...</span>';
      }
    }

    for (var i = startPage; i <= endPage; i++) {
      var activeClass = i === this.paginationActuelle ? ' active' : '';
      var pageUrl = this.construireUrlPage(i);
      paginationHTML += '<a href="' + pageUrl + '" class="custom-pagination-btn custom-pagination-num' + activeClass + '" data-page="' + i + '">' + i + '</a>';
    }

    // Toujours afficher la dernière page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        paginationHTML += '<span class="custom-pagination-ellipsis">...</span>';
      }
      var urlLast = this.construireUrlPage(totalPages);
      paginationHTML += '<a href="' + urlLast + '" class="custom-pagination-btn custom-pagination-num" data-page="' + totalPages + '">' + totalPages + '</a>';
    }

    // Lien suivant
    if (this.paginationActuelle < totalPages) {
      var nextUrl = this.construireUrlPage(this.paginationActuelle + 1);
      paginationHTML += '<a href="' + nextUrl + '" class="custom-pagination-btn" data-page="' + (this.paginationActuelle + 1) + '" rel="next">Suivant &raquo;</a>';
    }

    paginationHTML += '</nav>';

    this.elementsFiltre.grilleProduits.insertAdjacentHTML('afterend', paginationHTML);

    // Intercepter les clics pour navigation JS (sans rechargement de page)
    document.querySelectorAll('#custom-filters-pagination .custom-pagination-btn').forEach(function(btn) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        var page = parseInt(this.getAttribute('data-page'));
        self.paginationActuelle = page;
        
        // Mettre à jour l'URL dans la barre d'adresse sans recharger
        var newUrl = self.construireUrlPage(page);
        window.history.pushState({ page: page }, '', newUrl);
        
        self.afficherProduits();
      });
    });
  }

  /**
   * Changer le tri
   */
  changeSortie(sortie) {
    this.sortieActuelle = sortie;

    switch (sortie) {
      case 'price-ascending':
        this.produitsFiltrés.sort(function(a, b) { return (a.prix_min || a.prix) - (b.prix_min || b.prix); });
        break;
      case 'price-descending':
        this.produitsFiltrés.sort(function(a, b) { return (b.prix_min || b.prix) - (a.prix_min || a.prix); });
        break;
      case 'created-descending':
        // L'API ne fournit pas de date fiable, on garde l'ordre
        break;
      default:
        // Réappliquer les filtres pour revenir à l'ordre par défaut
        var self = this;
        this.produitsFiltrés = this.tousLesProduits.filter(function(p) {
          return self.produitCorrespondFiltre(p);
        });
        break;
    }

    this.paginationActuelle = 1;
    this.afficherProduits();
  }

  /**
   * Sauvegarder les filtres dans l'URL
   */
  sauvegarderFiltresVersURL() {
    var params = new URLSearchParams();

    Object.entries(this.filtresActifs).forEach(function(entry) {
      var categorie = entry[0];
      var valeurs = entry[1];
      if (valeurs.length > 0) {
        params.append('filter_' + categorie, valeurs.join(','));
      }
    });

    // Quand on applique des filtres, revenir à la page 1
    // (pas besoin de ?page=1, c'est la page par défaut)

    var nouvelleURL = params.toString()
      ? window.location.pathname + '?' + params.toString()
      : window.location.pathname;

    window.history.replaceState({}, '', nouvelleURL);
  }

  /**
   * Restaurer les filtres depuis l'URL
   */
  restaurerFiltresDepuisURL() {
    var params = new URLSearchParams(window.location.search);
    var aDesFiltres = false;

    var self = this;
    params.forEach(function(valeur, clé) {
      if (clé.indexOf('filter_') === 0) {
        var categorie = clé.replace('filter_', '');
        var valeurs = valeur.split(',');
        aDesFiltres = true;

        self.elementsFiltre.inputs.forEach(function(input) {
          if (
            input.getAttribute('data-filter') === categorie &&
            valeurs.indexOf(input.value) !== -1
          ) {
            input.checked = true;
          }
        });
      }
    });

    if (aDesFiltres) {
      this.mettreÀJourFiltres();
      this.appliquerFiltres();
    }
  }

  /**
   * Obtenir le handle de la collection depuis l'URL
   */
  obtenirCollectionHandle() {
    var match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    if (match) return match[1];
    return null;
  }

  /**
   * Extraire le prix d'une chaîne de texte
   */
  extrairePrix(texte) {
    var match = texte.match(/[\d]+[.,]?\d*/);
    if (match) return parseFloat(match[0].replace(',', '.'));
    return 0;
  }

  /**
   * Formater un prix en euros
   */
  formatPrix(prix) {
    if (!prix || isNaN(prix)) return '0€';
    var montant = parseFloat(prix);
    // Vérifier si c'est en centimes (> 1000 pour un prix de mariage, peu probable en euros)
    if (montant > 10000) montant = montant / 100;
    return montant.toFixed(2).replace('.', ',') + ' €';
  }

  /**
   * Échapper les caractères HTML
   */
  échapperHTML(texte) {
    if (!texte) return '';
    var div = document.createElement('div');
    div.textContent = texte;
    return div.innerHTML;
  }

  /**
   * Configuration par défaut
   */
  obtenirConfigParDéfaut() {
    return {
      filtres: [],
      tri: [],
      pagination: { par_page: 24, options: [24] }
    };
  }
}

// Initialiser une seule fois au chargement du DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function() {
    new FiltreCollection();
  });
} else {
  new FiltreCollection();
}